import { memo, useCallback } from 'react';
import { Send, Loader2, RefreshCw, ExternalLink, Lock, ArrowUp, ArrowDown } from 'lucide-react';

const QUICK_ACTIONS = [
  { label: 'Make shorter', prompt: 'Rewrite the copy in this section to be more concise — cut it by about 40%. Keep the same tone and message, just tighter.' },
  { label: 'Make longer', prompt: 'Expand the copy in this section with more detail and personality. Add 2-3 more sentences that feel authentic, not filler.' },
  { label: 'Warmer tone', prompt: 'Rewrite the copy to feel warmer, more personal, more human. First person. Like talking to a friend.' },
  { label: 'More luxury', prompt: 'Elevate the copy and visual styling to feel more premium and high-end. Tighter letter-spacing, more white space, refined language.' },
  { label: 'More direct', prompt: 'Rewrite the copy to be more direct and action-oriented. Cut fluff. Lead with the value proposition. Clear CTA.' },
  { label: 'Fix grammar', prompt: 'Fix any grammar, spelling, or punctuation errors in this section. Do not change the style, layout, or any images. Only fix text errors.' },
];

const SectionsTab = memo(({ sections, activeSection, setActiveSection, sectionChat, setSectionChat, sending, currentSuggestion, loadingSuggestion, fetchSuggestion, onSectionChat, onQuickAction, onReorder, userPlan, onShowPricing, onShowGallery, t, theme, inputStyle }) => {

  const handleQuickAction = useCallback((prompt) => {
    setSectionChat(prompt);
    setTimeout(() => {
      const form = document.querySelector(`[data-testid="section-form-${activeSection}"]`);
      if (form) form.requestSubmit();
    }, 50);
  }, [activeSection, setSectionChat]);

  return (
    <div>
      <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.muted, marginBottom: '14px' }}>
        Click a section to edit it
      </p>
      {sections.map((s, idx) => (
        <div key={s.id} style={{ marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Reorder arrows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
              {idx > 0 && (
                <button onClick={() => onReorder && onReorder(idx, idx - 1)} title="Move up" style={{ width: '22px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: t.muted, opacity: 0.3, borderRadius: '4px', transition: 'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = t.faint; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.3'; e.currentTarget.style.background = 'none'; }}>
                  <ArrowUp size={10} />
                </button>
              )}
              {idx < sections.length - 1 && (
                <button onClick={() => onReorder && onReorder(idx, idx + 1)} title="Move down" style={{ width: '22px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: t.muted, opacity: 0.3, borderRadius: '4px', transition: 'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = t.faint; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.3'; e.currentTarget.style.background = 'none'; }}>
                  <ArrowDown size={10} />
                </button>
              )}
            </div>
            {/* Section card */}
            <button
              onClick={() => {
                const newActive = activeSection === s.id ? null : s.id;
                setActiveSection(newActive);
                if (newActive) fetchSuggestion(s.type, s.id, s.label);
              }}
              data-testid={`section-btn-${s.type}`}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', borderRadius: '10px',
                background: activeSection === s.id ? (theme === 'light' ? '#fff' : 'rgba(201,168,76,0.06)') : (theme === 'light' ? '#fff' : 'rgba(242,239,233,0.02)'),
                border: `1px solid ${activeSection === s.id ? 'rgba(201,168,76,0.2)' : t.border}`,
                cursor: 'pointer', transition: 'all .2s',
                boxShadow: activeSection === s.id ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              }}
              onMouseEnter={e => { if (activeSection !== s.id) e.currentTarget.style.borderColor = 'rgba(201,168,76,0.12)'; }}
              onMouseLeave={e => { if (activeSection !== s.id) e.currentTarget.style.borderColor = t.border; }}
            >
              <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, color: t.fg }}>{s.label}</span>
              <span style={{ fontFamily: 'var(--sans)', fontSize: '10px', color: t.muted, textTransform: 'capitalize', opacity: 0.5 }}>{s.type}</span>
            </button>
          </div>

          {/* Expanded edit panel */}
          {activeSection === s.id && (
            <div style={{ padding: '12px 0 12px 26px', marginBottom: '4px' }}>
              {/* Quick Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '12px' }}>
                {QUICK_ACTIONS.map(action => (
                  <button
                    key={action.label}
                    onClick={() => handleQuickAction(action.prompt)}
                    disabled={sending}
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                    style={{
                      padding: '5px 12px', fontSize: '11px', fontFamily: 'var(--sans)', fontWeight: 500,
                      color: sending ? t.muted : t.gold,
                      background: 'rgba(201,168,76,0.04)',
                      border: '1px solid rgba(201,168,76,0.1)',
                      cursor: sending ? 'wait' : 'pointer',
                      transition: 'all .15s',
                      borderRadius: '20px',
                    }}
                    onMouseEnter={e => { if (!sending) { e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.25)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.04)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.1)'; }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>

              {/* AI Suggestion */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => setSectionChat(currentSuggestion[s.id] || '')}
                  data-testid={`suggestion-pill-${s.id}`}
                  style={{ flex: 1, padding: '10px 12px', fontSize: '12px', fontFamily: 'var(--sans)', color: t.gold, background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.08)', cursor: 'pointer', textAlign: 'left', transition: 'all .2s', borderRadius: '10px', lineHeight: 1.4 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.03)'}
                >
                  {loadingSuggestion ? 'Thinking...' : (currentSuggestion[s.id] || 'Loading suggestion...')}
                </button>
                <button
                  onClick={() => fetchSuggestion(s.type, s.id, s.label)}
                  disabled={loadingSuggestion}
                  style={{ width: '34px', height: '34px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.08)', cursor: loadingSuggestion ? 'wait' : 'pointer', color: t.gold, borderRadius: '10px', transition: 'all .15s' }}
                >
                  <RefreshCw size={12} className={loadingSuggestion ? 'animate-spin' : ''} />
                </button>
              </div>

              {/* Custom edit input */}
              <form onSubmit={onSectionChat} data-testid={`section-form-${s.id}`} style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text" value={sectionChat} onChange={e => setSectionChat(e.target.value)}
                  placeholder={`Tell Iris what to change...`}
                  disabled={sending}
                  data-testid={`section-chat-input-${s.id}`}
                  style={{ ...inputStyle, borderRadius: '10px' }}
                />
                <button type="submit" disabled={sending || !sectionChat.trim()} data-testid={`section-chat-send-${s.id}`} style={{
                  width: '36px', height: '36px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: sectionChat.trim() ? t.gold : t.faint, color: sectionChat.trim() ? '#000' : t.muted,
                  border: 'none', cursor: sectionChat.trim() ? 'pointer' : 'default', borderRadius: '10px', transition: 'all .15s',
                }}>
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                </button>
              </form>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', color: t.muted, marginTop: '8px', opacity: 0.6 }}>
                Only this section will change. Everything else stays.
              </p>
            </div>
          )}
        </div>
      ))}

      {/* Embed Gallery */}
      <button
        onClick={() => {
          if (userPlan && userPlan.plan === 'free') { onShowPricing(); return; }
          onShowGallery();
        }}
        data-testid="embed-gallery-btn"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '14px', marginTop: '14px', background: 'rgba(201,168,76,0.03)',
          border: `1px dashed rgba(201,168,76,0.15)`, cursor: 'pointer', transition: 'all .2s',
          borderRadius: '10px',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.06)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.03)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.15)'; }}
      >
        {userPlan && userPlan.plan === 'free' ? <Lock size={13} style={{ color: t.muted }} /> : <ExternalLink size={13} style={{ color: t.gold }} />}
        <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.gold }}>
          Embed Client Gallery
        </span>
      </button>
    </div>
  );
});

SectionsTab.displayName = 'SectionsTab';
export default SectionsTab;
